import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { TextField, Autocomplete, Typography, createFilterOptions, Skeleton } from "@mui/material";
import type { Commune } from "models";
import { communesService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import { stringUtil } from "@sdeapps/react-core";
import { grey } from "@mui/material/colors";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: (option: Commune) => {
    const normalizedString = stringUtil.normalize(option.libelle);
    return `${normalizedString} ${option.libelle}`;
  },
});

interface CommuneAutoCompleteProps {
  value: Commune | null;
  onChange?: (commune: Commune | null) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

function CommuneAutoComplete({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  disabled = false,
}: Readonly<CommuneAutoCompleteProps>): ReactNode {
  const [communes, setCommunes] = useState<Array<Commune>>([]);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getCommunes(): Promise<void> {
      const _communes = await communesService.getAll();
      setCommunes(_communes);
    }
    void catchErrors(getCommunes);
  }, [catchErrors]);

  return (
    <Autocomplete
      value={communes.find((c) => c.id === value?.id) ?? value ?? null}
      onChange={(_e, _value: Commune | null) => {
        onChange?.(_value);
      }}
      options={communes}
      filterOptions={filterOptions}
      fullWidth
      autoHighlight
      openOnFocus
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      size={size}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={({ libelle }) => libelle}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
      renderOption={(props, commune) => (
        <Typography component="li" {...props} key={commune.id}>
          {commune.libelle} <span className="grey">({commune.id.substring(0, 2)})</span>
        </Typography>
      )}
      slotProps={{
        paper: { sx: { "&.MuiAutocomplete-paper li span.grey": { color: grey[500], ml: 0.5 } } },
      }}
      noOptionsText={isLoading ? <Skeleton /> : "Pas de résultat"}
      disabled={disabled}
    />
  );
}

export const CommuneAutoCompleteWithErrorBoundary = withComponentErrorBoundary(CommuneAutoComplete);
